import {
  type PropsWithChildren,
  useCallback,
  useSyncExternalStore,
} from 'react'
import { createPortal } from 'react-dom'

import useWindowSize from '@v2/commons/hooks/useWindowSize.hook'

interface PortalTargetProps {
  className?: string
  id: string
  media?: 'desktop' | 'mobile'
  'data-testid'?: string
}

const PortalTarget = ({
  className,
  id,
  media = 'desktop',
  'data-testid': dataTestId,
}: PortalTargetProps) => (
  <div className={className} id={`${media}_${id}`} data-testid={dataTestId} />
)

interface PortalWrapperProps {
  id: string
  width?: number
  isAdaptive?: boolean
}

const getServerTarget = () => null

const PortalWrapper = ({
  id,
  width = 768,
  isAdaptive = true,
  children,
}: PropsWithChildren<PortalWrapperProps>) => {
  const windowSize = useWindowSize()
  const viewportWidth =
    windowSize.width || (typeof window !== 'undefined' ? window.innerWidth : 0)
  const media = !isAdaptive || viewportWidth >= width ? 'desktop' : 'mobile'
  const targetId = `${media}_${id}`

  const getTarget = useCallback(
    () =>
      typeof document === 'undefined'
        ? null
        : document.getElementById(targetId),
    [targetId],
  )
  const subscribe = useCallback((onTargetChange: () => void) => {
    // Targets can mount later or be replaced by route/tab navigation.
    const observer = new MutationObserver(onTargetChange)
    observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['id'],
    })
    return () => observer.disconnect()
  }, [])

  const target = useSyncExternalStore(subscribe, getTarget, getServerTarget)
  return target ? createPortal(children, target) : null
}

export default { Target: PortalTarget, Wrapper: PortalWrapper }
